import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { IClientPatch, IPatchAction } from "../../../../components/course-author/helpers/patches";
import { LocaleNames, PreferredLocale } from "../../../../utils/locales";
import { IUser } from "../../../Analytics/Common";
import {
  FieldSet,
  FormSelect,
  InputLink,
  SettingsInputItem,
  SetupButtonLink,
} from "../../../Common/Components/FormComponents";
import { Section } from "../../../Common/Styles/containers";
import { ICompleteOrganization } from "../../../types/common";
import { modifyOrganizationSettingsAliasNamePatch, modifyOrganizationSettingsLocalePatch } from "../helpers/patches";
import Admins from "./Admins";
import DangerZone from "./DangerZone";

interface IOrgSettingsTabProps {
  admins: IUser[];
  addNewAdmin: (adminId: string) => void;
  removeAdmin: (adminId: string) => void;
  accountId: string;
  organization: ICompleteOrganization;
  patchRequest: (patch: IClientPatch<IPatchAction>) => void;
}

const SectionSubTitle = styled.h4`
  margin: 2rem 0 2rem 0;
  font-weight: 600;
`;

interface ILocaleOptionProps {
  locale: PreferredLocale;
}

const LocaleOption: React.FC<ILocaleOptionProps> = props => (
  <option aria-label="language-select-option" value={props.locale}>
    {LocaleNames[props.locale]}
  </option>
);

type Request = "sent" | "not_sent" | "error";

const suggestUniqueName = (value: string) => {
  return value.replace(/[^a-zA-Z0-9]/g, "-");
};

const Settings: React.FC<IOrgSettingsTabProps> = props => {
  const allLocales = Object.keys(LocaleNames) as PreferredLocale[];

  const [input, setInput] = useState<string>(props.organization.aliases.elems[0]?.state.alias.name || "");
  const [language, setLanguage] = useState<PreferredLocale | undefined>(props.organization.defaultLocale);
  const [isValid, setIsValid] = useState<boolean>(true);
  const [hasBeenSent, setHasBeenSent] = useState<Request>("not_sent");

  const selectLocaleRef: React.RefObject<HTMLSelectElement> = React.createRef();
  const inputRef: React.RefObject<HTMLInputElement> = React.createRef();

  const modifyDefaultLocale = (defaultLanguage: PreferredLocale) => {
    const { organization, patchRequest, accountId } = props;

    if (!organization) {
      return;
    }

    const patch = modifyOrganizationSettingsLocalePatch(organization, accountId, defaultLanguage);
    patchRequest(patch);
  };

  const modifyUniqueName = (name: string) => {
    const { organization, patchRequest, accountId } = props;

    if (!organization) {
      return;
    }

    const patch = modifyOrganizationSettingsAliasNamePatch(organization, accountId, name);
    patchRequest(patch);
  };

  const selectDefaultLocale = () => {
    if (!selectLocaleRef.current) {
      return;
    }

    const value = selectLocaleRef.current.value as PreferredLocale;
    if (value !== language) {
      modifyDefaultLocale(value);
      setLanguage(value);
    }
  };

  const changeUniqueName = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!inputRef.current) {
      return;
    }

    const value = inputRef.current.value;
    const checkValid = !/[ `!@#$%^&*()_+\=\[\]{};':"\\|,.<>\/?~]/.test(value);

    if (!checkValid) {
      setIsValid(false);
      setInput(value);
      setHasBeenSent("error");
    } else if (value !== input) {
      setIsValid(true);
      modifyUniqueName(value);
      setInput(value);
      setHasBeenSent("sent");
    }
  };

  const changeSuggestedName = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    if (!inputRef.current) {
      return;
    }

    const value = inputRef.current.value;

    setIsValid(true);
    modifyUniqueName(value);
    setInput(value);
    setHasBeenSent("sent");
  };

  useEffect(() => {
    if (hasBeenSent === "sent") {
      const timeout = setTimeout(() => setHasBeenSent("not_sent"), 2000);
      return () => clearTimeout(timeout);
    }
    return;
  }, [hasBeenSent]);

  return (
    <div>
      <Section>
        <label htmlFor="unique-organisation-name">
          <SectionSubTitle>Unique Organisation Name</SectionSubTitle>
        </label>
        <FieldSet>
          <legend>Organisation Name</legend>
          <form onSubmit={changeUniqueName}>
            <InputLink
              aria-label="unique-org-input"
              name="unique-org-name"
              ref={inputRef}
              placeholder="Type here"
              defaultValue={input}
            />
            {!isValid && (
              <div>
                <label>
                  Sorry, the name '{input}' was either already taken or it contains invalid characters. We have
                  therefore assigned the name '{suggestUniqueName(input)}'.
                </label>
                <SetupButtonLink onClick={changeSuggestedName}>OK</SetupButtonLink>
              </div>
            )}
          </form>
        </FieldSet>
        {hasBeenSent === "sent" && <label>NAME UPDATED</label>}
        <label htmlFor="master-localization-select">
          <SectionSubTitle>Default language</SectionSubTitle>
        </label>
        <SettingsInputItem>
          <FormSelect
            aria-label="language-select"
            ref={selectLocaleRef}
            name="master-localization-select"
            value={language}
            onChange={selectDefaultLocale}
          >
            {allLocales.map(locale => (
              <LocaleOption key={locale} locale={locale} />
            ))}
          </FormSelect>
        </SettingsInputItem>
      </Section>

      <Section>
        <Admins
          admins={props.admins}
          addNewAdmin={props.addNewAdmin}
          removeAdmin={props.removeAdmin}
          accountId={props.accountId}
          organizationId={props.organization.organizationId}
        />
      </Section>

      <DangerZone />
    </div>
  );
};

export default Settings;
